/* Sign-in, and the token provider everything else reads through.

   Two modes, chosen by VITE_AUTH_MODE:

   - 'msal'  the real thing, against the app registration in docs/entra-setup.md
   - 'mock'  a fixed local user and a token provider that throws

   The mock mode exists so the app runs before the app registration does. It is
   not a bypass in any meaningful sense: it cannot produce a token, so nothing it
   returns will satisfy Graph or Key Vault. It only makes the shell renderable. */

import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
} from '@azure/msal-browser';
import { MsalProvider, useMsal } from '@azure/msal-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { ICurrentUser } from '../models';
import { AUTH_MODE, loginRequest, msalConfig } from './msalConfig';
import type { TokenProvider } from '../services/sharepoint/GraphClient';
import { PRODUCT } from '../lib/product';
/* The suite's own screens, not ones of Blueprint's.
   -------------------------------------------------------------------------
   Between pressing the Hub tile and the register appearing this is all anybody
   sees, and it was a bespoke card on a blueprint-grid background — a different
   product's loading screen. Waiting shows the Hub's launch splash, in whichever
   of its five designs the person chose there (the Hub hands that over on the
   tile's address); asking for a sign-in shows the Gate. */
import { Gate, GateMessage, GateAction, Splash, LAUNCH_MS, launchFromUrl } from '@hird/ui';
import '@hird/ui/gate.css';
import '@hird/ui/splash.css';

/* Read once, at startup, before anything can redirect. A first visit arrives as
   /?launch=dial and a sign-in redirect comes back without the query, so this has
   to be kept the moment it is seen. */
const LAUNCH = launchFromUrl();

/* A Hub-sized greeting for the Photo design, which is the only one that names
   anybody. */
const greetingNow = (): string => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
};

function LaunchSplash({ name }: { name?: string }) {
  return (
    <Splash
      style={LAUNCH}
      logo={PRODUCT.logo}
      title={PRODUCT.name}
      greeting={greetingNow()}
      name={name?.split(/\s+/)[0]}
      photo="/brand/launch-depot.jpg"
    />
  );
}

interface AuthValue {
  user: ICurrentUser | null;
  /** Acquires silently, falling back to a redirect only when interaction is required. */
  getToken: TokenProvider;
  signIn: () => void;
  signOut: () => void;
  ready: boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth outside AuthProvider');
  return value;
}

const MOCK_USER: ICurrentUser = {
  name: 'Simon Ford',
  upn: 'simon.ford@hird.co.uk',
  role: 'admin',
  scope: [],
};

/* ─────────────────────────── Mock ─────────────────────────── */

function MockAuthProvider({ children }: { children: ReactNode }) {
  const value = useMemo<AuthValue>(
    () => ({
      user: MOCK_USER,
      ready: true,
      getToken: async () => {
        throw new Error(
          'No token in mock auth mode. Set VITE_AUTH_MODE=msal once the app registration exists — see docs/entra-setup.md.',
        );
      },
      signIn: () => undefined,
      signOut: () => undefined,
    }),
    [],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/* ─────────────────────────── MSAL ─────────────────────────── */

/** Role comes from Entra group membership, surfaced as app roles on the token.
    This decides what to *render* and is explicitly not a security boundary —
    every restriction that matters is a real SharePoint permission (§9). */
function roleFromClaims(account: AccountInfo): ICurrentUser['role'] {
  const roles = (account.idTokenClaims as { roles?: string[] } | undefined)?.roles ?? [];
  if (roles.includes('Blueprint.Admin')) return 'admin';
  if (roles.includes('Blueprint.Manager')) return 'manager';
  if (roles.includes('Blueprint.Finance')) return 'finance';
  return 'reader';
}

/* MSAL's quiet renewal runs in a hidden iframe, and this app tells browsers never to
   frame it (X-Frame-Options: DENY), so that route cannot work here. When there is no
   cached token for a resource and the refresh token cannot supply one, MSAL tries the
   iframe, waits ten seconds, and throws one of these instead of
   InteractionRequiredAuthError: the person was told "Token acquisition in iframe failed
   due to timeout" and never offered a sign-in. It means the same thing, so it is
   treated the same way. */
const IFRAME_FAILURES = new Set([
  'monitor_window_timeout',
  'empty_window_error',
  'hash_empty_error',
  'timed_out',
]);

function needsInteraction(err: unknown): boolean {
  if (err instanceof InteractionRequiredAuthError) return true;
  return err instanceof BrowserAuthError && IFRAME_FAILURES.has(err.errorCode);
}

/* WHO THE HUB SAYS YOU ARE.
   Blueprint is a different site from the Hub, so it has its own sign-in and its own
   browser cache, and nothing is carried over by itself. Left alone it signs in as
   whichever account Microsoft finds first: the first one in this site's cache, or the
   browser's default session. On a machine that has also been used for admin work that
   is not necessarily you, and what you can do here depends on who you are: the
   onboarding Function refused somebody who was signed into the Hub as themselves and
   into Blueprint as the IT admin account.

   The Hub puts the sign-in name on the address as ?login_hint=, as it already does for
   the theme and the launch design. It is taken off the address at once so it is not
   left in history or a bookmark, and every later load behaves as before. */
function takeLoginHint(): string | undefined {
  try {
    const url = new URL(window.location.href);
    const hint = url.searchParams.get('login_hint')?.trim();
    if (!hint) return undefined;
    url.searchParams.delete('login_hint');
    window.history.replaceState(null, '', url.toString());
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(hint) ? hint : undefined;
  } catch {
    return undefined;
  }
}

const LOGIN_HINT = takeLoginHint();

function MsalAuthProvider({ children }: { children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  /* The account getMsal() chose, not simply the first in the list. */
  const active = instance.getActiveAccount();
  const account = accounts.find((a) => a.homeAccountId === active?.homeAccountId) ?? accounts[0];

  const getToken = useCallback<TokenProvider>(
    async (scopes) => {
      if (!account) throw new Error('Not signed in');
      try {
        const result = await instance.acquireTokenSilent({ scopes, account });
        return result.accessToken;
      } catch (err) {
        /* Only redirect when MSAL says interaction is genuinely required.
           Redirecting on any failure turns a transient network blip into a
           sign-in loop — and offline, into an unusable app. */
        if (needsInteraction(err)) {
          await instance.acquireTokenRedirect({ scopes, account });
        }
        throw err;
      }
    },
    [instance, account],
  );

  /* Hold the gate until there is a token, not just an account.
     -------------------------------------------------------------------------
     The gate used to lift the moment MSAL produced a cached account, which on
     a return visit is a few hundred milliseconds — so the loading state
     existed but nobody ever saw it, and the app appeared with its tables
     still empty while the first token was fetched behind them. "No loader" is
     what that looks like.

     SignIn's gate stays up because it probes access on every load. This is
     the same idea with the step Blueprint actually needs: acquire the token
     once, up front, and show the gate while it happens. Nothing in the app
     can do anything without one, so this is not an artificial wait — it is
     the wait, made visible.

     A failure does NOT hold the gate shut. acquireTokenSilent redirects by
     itself when interaction is genuinely required; anything else — a blip, a
     brief offline — must let the app render, because the services below
     handle their own errors and a gate that never lifts is worse than a
     screen that reports one. */
  const [tokenReady, setTokenReady] = useState(false);

  useEffect(() => {
    if (!account) return;
    let live = true;
    /* The same scopes the sign-in asked for, so this is the token the app
       will actually use rather than a second, narrower one. */
    /* A ceiling on the wait.

       A rejection already lifts the gate, via the catch below. A promise that
       never settles would not — acquireTokenSilent can sit on a slow or
       captive network — and a loading screen nobody can get past is the one
       failure worse than the empty tables this replaced. Eight seconds, then
       the app renders and the services report whatever is actually wrong. */
    const ceiling = new Promise<void>(resolve => {
      window.setTimeout(resolve, 8000);
    });

    /* A floor as well as a ceiling: a cached token resolves in a few hundred
       milliseconds, which would cut the splash off a fraction of the way
       through its animation. Held for one full run of it — the same time the
       Hub's own cover plays — so it reads as the screen it is. */
    const floor = new Promise<void>(resolve => {
      window.setTimeout(resolve, LAUNCH_MS);
    });

    void Promise.all([
      Promise.race([getToken(loginRequest.scopes).catch(() => undefined), ceiling]),
      floor,
    ]).then(() => { if (live) setTokenReady(true); });
    return () => { live = false; };
  }, [account, getToken]);

  const value = useMemo<AuthValue>(
    () => ({
      user: account
        ? {
            name: account.name ?? account.username,
            upn: account.username,
            role: roleFromClaims(account),
            scope: [],
          }
        : null,
      ready: inProgress === 'none',
      getToken,
      signIn: () => void instance.loginRedirect(loginRequest),
      signOut: () => void instance.logoutRedirect(),
    }),
    [account, inProgress, getToken, instance],
  );

  /* Gate rather than let the app render signed-out.

     The service layer falls back to mock providers whenever there is no user, so
     a signed-out app in sharepoint mode renders seed data under a sidebar that
     says "SharePoint". Sample data passing for a real register is the exact
     failure this app is meant to prevent, so there is no signed-out state to get
     into: no account, no app. */
  if (!account) {
    /* Mid-redirect. Rendering the gate here would flash a sign-in prompt at
       someone who is already signing in. */
    if (inProgress !== 'none') {
      return <LaunchSplash />;
    }

    return (
      <Gate logo={PRODUCT.logo} title={PRODUCT.name}>
        <GateMessage>{PRODUCT.signInHint}</GateMessage>
        <GateAction primary onClick={() => void instance.loginRedirect(loginRequest)}>
          <MicrosoftLogo />
          Sign in with Microsoft
        </GateAction>
      </Gate>
    );
  }

  /* Signed in, but not yet able to ask the API anything. */
  if (!tokenReady) return <LaunchSplash name={account.name ?? account.username} />;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/* Microsoft's own mark, in the colours it is only ever drawn in. Inline rather
   than fetched: a sign-in button waiting on a network request to look finished
   is a sign-in button that looks broken on a slow morning. */
function MicrosoftLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden focusable="false">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

/* ─────────────────────────── Entry point ─────────────────────────── */

/* One instance, initialised once, at module scope.

   Creating it inside the effect looks harmless and is not: StrictMode mounts
   effects twice in development, so two PublicClientApplications each called
   handleRedirectPromise(). The first consumes the redirect state, the second
   finds it gone and throws

       state_not_found: State not found: Cached State

   which surfaces as "Sign-in could not start" and reads exactly like a wrong
   client id or an unregistered redirect URI. Holding the promise rather than the
   instance means the second caller awaits the first init instead of racing it. */
let msalReady: Promise<PublicClientApplication> | null = null;

function getMsal(): Promise<PublicClientApplication> {
  if (!msalReady) {
    const instance = new PublicClientApplication(msalConfig);
    msalReady = instance
      .initialize()
      .then(() => instance.handleRedirectPromise())
      .then(async () => {
        const all = instance.getAllAccounts();
        const hinted = LOGIN_HINT
          ? all.find((a) => a.username.toLowerCase() === LOGIN_HINT.toLowerCase())
          : undefined;
        /* Told who to be, and not signed in as them here: sign in as them. A redirect,
           not a hidden iframe, because this app forbids being framed. The hint is
           already off the address, so coming back cannot loop. If they have no session,
           Microsoft asks them to sign in with the name filled in. */
        if (LOGIN_HINT && !hinted) {
          await instance.loginRedirect({ ...loginRequest, loginHint: LOGIN_HINT });
        }
        const account = hinted ?? instance.getActiveAccount() ?? all[0];
        if (account) instance.setActiveAccount(account);
        return instance;
      });
  }
  return msalReady;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [msal, setMsal] = useState<PublicClientApplication | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    if (AUTH_MODE !== 'msal') return;
    let cancelled = false;
    getMsal()
      .then((instance) => {
        if (!cancelled) setMsal(instance);
      })
      .catch((err: unknown) => {
        if (!cancelled) setFailed(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (AUTH_MODE !== 'msal') return <MockAuthProvider>{children}</MockAuthProvider>;

  if (failed) {
    return (
      <div style={{ padding: 32, maxWidth: 560, margin: '0 auto' }}>
        <h1>Sign-in could not start</h1>
        <p style={{ marginTop: 12 }}>{failed}</p>
        <p style={{ marginTop: 12, color: 'var(--muted)' }}>
          Check VITE_CLIENT_ID and VITE_TENANT_ID, and that this origin is a registered
          redirect URI. See docs/entra-setup.md.
        </p>
        {/* Named explicitly because it is a stale-storage problem wearing a
            configuration problem's clothes, and the advice above sends you off
            checking a registration that was fine all along. */}
        {failed.includes('state_not_found') && (
          <p style={{ marginTop: 12, color: 'var(--muted)' }}>
            <code>state_not_found</code> usually means a half-finished sign-in is still in
            storage rather than anything being misconfigured. Clear site data for this origin
            and reload.
          </p>
        )}
      </div>
    );
  }

  if (!msal) return null;

  return (
    <MsalProvider instance={msal}>
      <MsalAuthProvider>{children}</MsalAuthProvider>
    </MsalProvider>
  );
}
